import React, { useEffect, useState, useMemo } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import getMeta from '@atlassian/jira-get-meta';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { RESOURCE_TYPE_NOOP } from '@atlassian/jira-router-resources-utils/src/constants.tsx';
import { SpaStateActionsSubscriber } from '@atlassian/jira-spa-state-controller/src/common/index.tsx';
import { useSpaStateTransition } from '@atlassian/jira-spa-state-controller/src/components/transition-state/index.tsx';
import type { ResourceMetrics } from '@atlassian/jira-spa-state-controller/src/types.tsx';
import {
	useRouter,
	useResource,
	type Match,
	type RouteResource,
	type Route,
} from '@atlassian/react-resource-router';
import { clearPrefetchStartMarks, getPrefetchStartMark } from './utils/prefetch-timing';

type AnalyticsObject = {
	action: string;
};
type Props = {
	// createAnalyticsEvent prop is coming from Atlaskit, no control over it
	createAnalyticsEvent: (data: AnalyticsObject) => UIAnalyticsEvent;
	setResourceMetrics: (events: ResourceMetrics) => void;
};

type ResourceRequestMetrics = {
	duration: number | null;
	end: number | null;
	isCacheHit: boolean | null;
	start: number;
	type: string | null;
};

type ResourceMetricTrackerProps = {
	isInitialRender: boolean;
	resource: RouteResource<unknown>;
	route: Route;
	createAnalyticsEvent: Props['createAnalyticsEvent'];
	setResourceMetrics: Props['setResourceMetrics'];
	currentMatch: Match;
};

const defaultMetrics: ResourceRequestMetrics = {
	type: null,
	duration: null,
	end: null,
	isCacheHit: null,
	start: 0,
};

const canSendMetrics = ({ duration, end, isCacheHit, start }: ResourceRequestMetrics): boolean =>
	isCacheHit !== defaultMetrics.isCacheHit &&
	duration !== defaultMetrics.duration &&
	start !== defaultMetrics.start &&
	end !== defaultMetrics.end;

const isCachedResource = (slice: ReturnType<typeof useResource>, invocationTimestamp: number) =>
	!slice.loading && (slice.expiresAt ?? 0) > invocationTimestamp;

const SAMPLING_RATE = 10;

const ResourceMetricTracker = ({
	resource,
	route: currentRoute,
	createAnalyticsEvent,
	setResourceMetrics,
	currentMatch,
	isInitialRender,
}: ResourceMetricTrackerProps) => {
	const [metrics, setMetrics] = useState<ResourceRequestMetrics>(defaultMetrics);
	const [isSent, setIsSent] = useState<boolean>(false);

	const slice = useResource(resource);

	useEffect(
		() =>
			// clear metrics on route change
			() => {
				setMetrics({ ...defaultMetrics });
				setIsSent(false);
			},
		[currentRoute],
	);

	// watch the resource and update metrics
	useEffect(() => {
		if (slice.expiresAt === null || isSent) {
			// don't record metrics until the slice has an expiry set
			return;
		}

		const invocationTimestamp = Date.now();

		if (metrics.isCacheHit === null && isCachedResource(slice, invocationTimestamp)) {
			// cached resource
			setMetrics((state) => ({
				...state,
				duration: 0,
				end: invocationTimestamp,
				isCacheHit: true,
				start: invocationTimestamp,
				type: resource.type,
			}));
			return;
		}

		if (slice.loading && metrics.start === 0) {
			// set start time
			setMetrics((state) => ({
				...state,
				type: resource.type,
				isCacheHit: false,
				start: invocationTimestamp,
			}));
			return;
		}

		if (
			!slice.loading &&
			metrics.start !== defaultMetrics.start &&
			metrics.end === defaultMetrics.end
		) {
			// set end time
			setMetrics((state) => ({
				...state,
				duration: invocationTimestamp - state.start,
				end: invocationTimestamp,
			}));
		}
	}, [isSent, currentRoute, metrics, resource, slice]);

	// watch the metrics object and send event when it has enough data
	useEffect(() => {
		const { error } = slice;

		if (
			isSent ||
			!canSendMetrics(metrics) ||
			// Always send error events but sample success events (1 in every 10)
			(!error && Math.random() * 100 > SAMPLING_RATE)
		) {
			return;
		}

		const { isCacheHit, duration, start, end, type: resourceType } = metrics;

		const action = error ? 'fetchFailed' : 'fetchSucceeded';
		const analyticsEvent = createAnalyticsEvent({ action });
		const errorMessage = error && error instanceof Error ? error.toString() : error;
		const routeName = currentRoute.name ? currentRoute.name : 'empty';

		fireOperationalAnalytics(analyticsEvent, `${action} resource`, resource.type, {
			duration,
			isCacheHit,
			start,
			end,
			routeName,
			prefetchStartTime: getPrefetchStartMark(currentMatch),
			isInitialRender,
			resourceType,
			...(errorMessage !== null && errorMessage !== undefined ? { error: errorMessage } : {}),
		});

		if (error) {
			fireErrorAnalytics({
				meta: {
					id: `spaTransitionResources.${(resourceType || '').toLowerCase()}`,
					packageName: 'jiraSpaTransitionResourcesTracker',
					teamName: 'magma',
				},
				attributes: {
					// Filter out synthetic tests using all known ways
					isSynthetic:
						getMeta('ajs-is-synthetic') === 'true' || ff('issue.details.forge-ui-synthetic-check'),
				},
				// @ts-expect-error - TS2322 - Type 'Error | Record<string, any> | (Error & Record<string, any>) | (Record<string, any> & Error)' is not assignable to type 'Error | undefined'.
				error,
				skipSentry: true,
			});
			log.safeErrorWithoutCustomerData(
				`resources.${(resourceType || '').toLowerCase()}`,
				// @ts-expect-error - TS2345 - Argument of type 'string | Record<string, any>' is not assignable to parameter of type 'string'.
				errorMessage || '',
				error,
			);
		}

		// add the resource metrics to the spa state store
		setResourceMetrics({
			// @ts-expect-error - TS2322 - Type 'number | null' is not assignable to type 'number'.
			end,
			// @ts-expect-error - TS2322 - Type 'boolean | null' is not assignable to type 'boolean'.
			isCacheHit,
			start,
			// @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'string'.
			type: resourceType,
		});

		setIsSent(true);
	}, [
		createAnalyticsEvent,
		currentRoute,
		isSent,
		metrics,
		setResourceMetrics,
		slice,
		resource,
		currentMatch,
		isInitialRender,
	]);

	return null;
};

export const SpaResourceMetricsController = ({
	createAnalyticsEvent,
	setResourceMetrics,
}: Props) => {
	const [{ route: currentRoute, match: currentMatch }] = useRouter();
	const [{ isInitialRender }] = useSpaStateTransition();

	useEffect(() => {
		clearPrefetchStartMarks(currentMatch);
	}, [currentMatch]);

	const resourcesToTrack = useMemo(() => {
		// exclude resources that are turned off by feature flags
		const resources: RouteResource<unknown>[] =
			currentRoute?.resources?.filter(
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(resource: RouteResource<any>) => resource.type !== RESOURCE_TYPE_NOOP,
			) || [];

		return resources.map((resource) => (
			<ResourceMetricTracker
				key={resource.type}
				resource={resource}
				route={currentRoute}
				currentMatch={currentMatch}
				createAnalyticsEvent={createAnalyticsEvent}
				setResourceMetrics={setResourceMetrics}
				isInitialRender={isInitialRender}
			/>
		));
		// disabling lint as we are triggering the re-render with props that are not directly used
	}, [currentRoute, currentMatch, createAnalyticsEvent, setResourceMetrics, isInitialRender]);

	return <>{resourcesToTrack}</>;
};

type SpaResourceMetricsControllerWithActionsProps = {
	createAnalyticsEvent: Props['createAnalyticsEvent'];
};

export const SpaResourceMetricsControllerWithActions = ({
	createAnalyticsEvent,
}: SpaResourceMetricsControllerWithActionsProps) => (
	// @ts-expect-error - TS2745 - This JSX tag's 'children' prop expects type 'never' which requires multiple children, but only a single child was provided.
	<SpaStateActionsSubscriber>
		{/* @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7031 - Binding element 'setResourceMetrics' implicitly has an 'any' type. */}
		{(_, { setResourceMetrics }) => (
			<SpaResourceMetricsController
				setResourceMetrics={setResourceMetrics}
				createAnalyticsEvent={createAnalyticsEvent}
			/>
		)}
	</SpaStateActionsSubscriber>
);
